import * as React from "react"
import { Row } from "react-grid-system"
import Subtitle from "../../atoms/typography/Subtitle"
import styled from "styled-components"
import { MediaQuery } from "../../../utils/design-helpers"
import TextInput from "../../molecules/TextInput"
import { checkEmailValid } from "../../../utils/helpers"
import { useLogin } from "../../../hooks/useLogin"
import H3 from "../../atoms/typography/H3"
import { theme } from "../../../components/theme"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import TextButton from "../../atoms/buttons/TextButton"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import BodyMedium from "../../atoms/typography/BodyMedium"
import { MailIcon } from "../../atoms/icons/MailIcon"
import { ContactPhoneIcon } from "../../atoms/icons/ContactPhoneIcon"
import { captureExceptionWithContext } from "../../../utils/sentry"
import { ApolloError } from "@apollo/client"

const Content = styled.div`
  padding: 2rem 3rem;
  width: 40vw;

  ${MediaQuery.SM} {
    width: 40vw;
  }

  ${MediaQuery.XS} {
    width: 90vw;
    padding: 1rem;
  }
`

const SupportInfo = styled.div`
  display: flex;
  align-items: center;
`

interface Props {
  onClickForgotPassword: () => void
  onClickRegister: () => void
  onCloseModal: () => void
  onGoTo2fa: () => void
  setUserHasBackupTokens: (x: boolean) => void
  redirectUrl?: string
}

const LoginCard: React.FunctionComponent<Props> = ({
  onClickForgotPassword,
  onClickRegister,
  onCloseModal,
  onGoTo2fa,
  setUserHasBackupTokens,
  redirectUrl,
}) => {
  const [email, setEmail] = React.useState("")
  const [password, setPassword] = React.useState("")

  const onCompletedLogIn = (data: any) => {
    if (data?.login?.loggedInSuccessfully) {
      onCloseModal()
      if (redirectUrl) {
        window.location.href = redirectUrl
      }
    }

    if (data?.login?.hasBackupTokens) {
      setUserHasBackupTokens(true)
    } else {
      setUserHasBackupTokens(false)
    }

    if (data?.login?.hasTotpDevice) {
      onGoTo2fa()
    }
  }
  const onErrorLogIn = (error: ApolloError) => {
    captureExceptionWithContext(error, "Error in login")
  }
  const { login, data, loading, loginError } = useLogin({
    onCompleted: (data: any) => onCompletedLogIn(data),
    onError: onErrorLogIn,
  })
  const onLogIn = async (event: any, email: string, password: string) => {
    event.preventDefault()

    if (!!checkEmailValid(email) || password.length < 1) {
      try {
        await login({
          variables: {
            email: email,
            password: password,
          },
        })
      } catch (error) {
        captureExceptionWithContext(error, "Error in login")
      }
    } else {
      console.log("fill in form")
    }
  }

  const showSupportInfo = redirectUrl?.includes("support")

  return (
    <Row justify="center">
      <Content>
        <HorizontalSpacer spacing={1} />
        <Subtitle textAlign="center">Log in to Satcube</Subtitle>
        <HorizontalSpacer spacing={3} />
        <form onSubmit={(e) => onLogIn(e, email, password)}>
          <TextInput textFieldTitle="Email" onChange={(e) => setEmail(e.target.value)} type="email" tabIndex={1} />
          <HorizontalSpacer spacing={3} />
          <TextInput
            textFieldTitle="Password"
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            tabIndex={2}
          />
          <HorizontalSpacer spacing={3} />
          <Row justify="center">
            <TextButton onClick={onClickForgotPassword} title="Forgot your password?" />
          </Row>
          {data?.login?.errors?.length > 0 && (
            <div>
              <HorizontalSpacer spacing={1} />
              <H3 color={theme.colors.red}>{data.login.errors}</H3>
            </div>
          )}
          <HorizontalSpacer spacing={3} />
          <Row justify="center">
            <PrimaryButton title="Register" onClick={onClickRegister} type="button" />
            <Spacer spacing={2} />
            {loading ? (
              <LoadingSpinner />
            ) : (
              <SecondaryButton title="Log in" type="submit" disabled={!checkEmailValid(email) || password.length < 1} />
            )}
          </Row>
        </form>
        <HorizontalSpacer spacing={1} />
        {showSupportInfo && (
          <div>
            <HorizontalSpacer spacing={3} />
            <Row justify="around" align="end">
              <div>
                <BodyMedium>Contact support directly:</BodyMedium>
                <HorizontalSpacer spacing={1} />
                <SupportInfo>
                  <MailIcon />
                  <Spacer spacing={0.5} />
                  <a href="mailto:support@satcube.com">
                    <BodyMedium>support@satcube.com</BodyMedium>
                  </a>
                </SupportInfo>
              </div>
              <SupportInfo>
                <ContactPhoneIcon />
                <Spacer spacing={0.5} />
                <a href="tel:0046317010500">
                  <BodyMedium>+46 31 701 05 00 (intl)</BodyMedium>
                </a>
              </SupportInfo>
              <SupportInfo>
                <ContactPhoneIcon />
                <Spacer spacing={0.5} />
                <a href="tel:0018886175180">
                  <BodyMedium>+1 888 6175 180 (US)</BodyMedium>
                </a>
              </SupportInfo>
            </Row>
          </div>
        )}
      </Content>
    </Row>
  )
}

export default LoginCard
